import type { CreateOwnerInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input } from 'antd';
import { UserRoundCog } from 'lucide-react';
import { PayoutMethodFields } from './PayoutMethodFields';

interface CreateOwnerDrawerProps {
  open: boolean;
  error: string | null;
  submitting: boolean;
  onCreate: (input: CreateOwnerInput) => Promise<boolean>;
  onClose: () => void;
}

/** Name, phone, email, KRA PIN and payout details — an owner has no contacts or status of its own, unlike Customer. */
export function CreateOwnerDrawer({ open, error, submitting, onCreate, onClose }: CreateOwnerDrawerProps) {
  const [form] = Form.useForm<CreateOwnerInput>();

  async function submit(values: CreateOwnerInput) {
    const created = await onCreate(values);
    if (created) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create owner" placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<CreateOwnerInput>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={{ payoutDetails: { method: 'mpesa' } }}
      >
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input prefix={<UserRoundCog className="size-4 text-muted" />} autoFocus />
        </Form.Item>
        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Enter a phone number' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
            <Input />
          </Form.Item>
        </div>
        <Form.Item
          name="kraPin"
          label="KRA PIN"
          rules={[{ required: true, pattern: /^[A-Za-z]\d{9}[A-Za-z]$/, message: 'One letter, nine digits, one letter' }]}
        >
          <Input className="uppercase" />
        </Form.Item>
        <PayoutMethodFields />
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create owner
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
